'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, CalendarDays, Clock, MapPin, FileText, Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface BookingForm {
  serviceType: string;
  date: string;
  startTime: string;
  endTime: string;
  suburb: string;
  notes: string;
}

const suburbs = [
  'Arcadia', 'Brooklyn', 'Centurion', 'Faerie Glen', 'Garsfontein',
  'Hatfield', 'Lynnwood', 'Menlyn', 'Meyerspark', 'Montana',
  'Moreleta Park', 'Muckleneuk', 'Queenswood', 'Rietfontein',
  'Silver Lakes', 'Sunnyside', 'Waterkloof', 'Waterkloof Ridge',
];

const serviceTypes = [
  'Elder care — full day',
  'Elder care — half day',
  'Companionship visit',
  'Medication reminder check',
  'Mobility assistance',
  'Overnight care',
];

interface Props {
  onClose: () => void;
  workerName: string;
  workerId?: string;
}

export default function BookingRequestModal({ onClose, workerName, workerId }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [formData, setFormData] = useState<BookingForm | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<BookingForm>();
  const { user } = useAuth();

  const onSubmit = async (data: BookingForm) => {
    setFormData(data);
    setStep('payment');
  };

  const handlePayWithStitch = async () => {
    if (!formData) return;
    setSubmitting(true);

    try {
      const externalReference = `booking-${workerId || 'worker'}-${Date.now()}`;
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const redirectUri = `${siteUrl}/payment/callback`;

      const res = await fetch('/api/stitch/payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: 100,
          externalReference,
          payerEmail: user?.email || '',
          payerName: user?.user_metadata?.full_name || '',
          redirectUri,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error('Payment setup failed', { description: data.error || 'Could not create payment request.' });
        setSubmitting(false);
        return;
      }

      // Redirect to Stitch payment page
      window.location.href = data.paymentUrl;
    } catch {
      toast.error('Something went wrong', { description: 'Please try again.' });
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(15,26,46,0.5)' }}>
      <div className="bg-white rounded-2xl w-full max-w-lg card-shadow-lg animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <h2 className="font-extrabold" style={{ color: 'var(--navy)' }}>
              {step === 'form' ? 'Request a booking' : 'Confirm & Pay'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>with {workerName}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-muted transition-colors">
            <X size={18} />
          </button>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
            {/* Service type */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                Service type <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <div className="relative">
                <FileText size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                <select {...register('serviceType', { required: 'Please select a service type' })} className="input-field pl-9 appearance-none">
                  <option value="">Select service type</option>
                  {serviceTypes.map((s) => <option key={`svc-${s}`} value={s}>{s}</option>)}
                </select>
              </div>
              {errors.serviceType && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.serviceType.message}</p>}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                Date <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <div className="relative">
                <CalendarDays size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                <input type="date" {...register('date', { required: 'Please select a date' })} className="input-field pl-9" />
              </div>
              {errors.date && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.date.message}</p>}
            </div>

            {/* Time range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                  Start time <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <div className="relative">
                  <Clock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                  <input type="time" {...register('startTime', { required: 'Required' })} className="input-field pl-9" />
                </div>
                {errors.startTime && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.startTime.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                  End time <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <div className="relative">
                  <Clock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                  <input type="time" {...register('endTime', { required: 'Required' })} className="input-field pl-9" />
                </div>
                {errors.endTime && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.endTime.message}</p>}
              </div>
            </div>

            {/* Suburb */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                Your suburb <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Suburb only — your exact address will be shared 3 hours before your confirmed booking.</p>
              <div className="relative">
                <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                <select {...register('suburb', { required: 'Please select your suburb' })} className="input-field pl-9 appearance-none">
                  <option value="">Select your suburb</option>
                  {suburbs.map((s) => <option key={`booking-suburb-${s}`} value={s}>{s}</option>)}
                </select>
              </div>
              {errors.suburb && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.suburb.message}</p>}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                Additional notes
              </label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Any relevant context — do not share contact details or addresses here.</p>
              <textarea
                {...register('notes')}
                rows={3}
                placeholder="e.g. My mother has moderate Alzheimer's and uses a walking frame..."
                className="input-field resize-none"
              />
            </div>

            {/* Fee note */}
            <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'var(--orange-pale)' }}>
              <span className="text-xs leading-relaxed" style={{ color: 'var(--primary)' }}>
                <strong>R100 booking fee</strong> is charged after you review and confirm. Paid securely via Stitch (EFT or card).
              </span>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 btn-outline">Cancel</button>
              <button type="submit" className="flex-1 btn-primary">
                Review & Pay
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-5">
            {/* Summary */}
            <div className="rounded-2xl p-4 space-y-2" style={{ background: 'var(--muted)' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Booking Summary</p>
              {formData && (
                <>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--muted-foreground)' }}>Service</span>
                    <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{formData.serviceType}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--muted-foreground)' }}>Date</span>
                    <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{formData.date}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--muted-foreground)' }}>Time</span>
                    <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{formData.startTime} – {formData.endTime}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--muted-foreground)' }}>Suburb</span>
                    <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{formData.suburb}</span>
                  </div>
                </>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between" style={{ borderColor: 'var(--border)' }}>
                <span className="font-bold text-sm" style={{ color: 'var(--navy)' }}>Booking Fee</span>
                <span className="font-extrabold text-lg" style={{ color: 'var(--primary)' }}>R100</span>
              </div>
            </div>

            <div className="rounded-xl p-3 text-xs leading-relaxed" style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}>
              You will be redirected to Stitch to complete your payment securely via EFT or card. Once paid, the candidate's contact details will be unlocked.
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep('form')} className="flex-1 btn-outline">
                Back
              </button>
              <button
                type="button"
                onClick={handlePayWithStitch}
                disabled={submitting}
                className="flex-1 btn-primary disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" />Redirecting...</>
                ) : (
                  <><CreditCard size={16} />Pay R100 via Stitch</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}